/**
 * shoppingListItemThumb — the per-item emoji thumbnail, shared by the Planning
 * row (`ShoppingListItemRow`) and the Shopping row (`ShoppingModeView` `CheckRow`)
 * so both surfaces render the SAME glyph for a given item from one source.
 *
 * The catalog has no per-item image on the list join, so the thumb is a glyph
 * keyed off the item name (with the product category as a secondary signal).
 * The prototype shows a per-item emoji (🍶🥛🥚🍞); an exact-name map missed real
 * product names ("2% Milk", "Apples", "Whole Milk") and the whole column fell
 * back to an identical 🛒. Matching is keyword / substring so name variants
 * resolve, like the prototype.
 *
 * Ordered most-specific → least so "soy sauce" wins over "soy", and a fruit
 * keyword wins before the broad produce default. Real product images are out of
 * scope; 🛒 stays only as the genuine-unknown fallback.
 */

import type { ShoppingListItemWithDetails } from '@shopista/shared/types';

const NAME_EMOJI: ReadonlyArray<readonly [string, string]> = [
  // Pantry / condiments (specific compound names first).
  ['soy sauce', '🍶'],
  ['olive oil', '🫒'],
  ['oil', '🫒'],
  ['sauce', '🍶'],
  ['vinegar', '🍶'],
  ['honey', '🍯'],
  ['jam', '🍯'],
  ['peanut butter', '🥜'],
  ['cereal', '🥣'],
  ['oats', '🥣'],
  ['rice', '🍚'],
  ['pasta', '🍝'],
  ['noodle', '🍜'],
  ['flour', '🌾'],
  ['sugar', '🧂'],
  ['salt', '🧂'],
  ['coffee', '☕'],
  ['tea', '🍵'],
  ['soup', '🥫'],
  ['beans', '🫘'],
  // Dairy & eggs.
  ['yogurt', '🥛'],
  ['yoghurt', '🥛'],
  ['milk', '🥛'],
  ['cream', '🥛'],
  ['cheese', '🧀'],
  ['butter', '🧈'],
  ['egg', '🥚'],
  // Bakery.
  ['bread', '🍞'],
  ['bagel', '🥯'],
  ['baguette', '🥖'],
  ['croissant', '🥐'],
  // Produce — fruit.
  ['apple', '🍎'],
  ['banana', '🍌'],
  ['orange', '🍊'],
  ['lemon', '🍋'],
  ['grape', '🍇'],
  ['strawberr', '🍓'],
  ['berr', '🫐'],
  ['watermelon', '🍉'],
  ['peach', '🍑'],
  ['pear', '🍐'],
  ['cherr', '🍒'],
  ['avocado', '🥑'],
  // Produce — vegetables.
  ['tomato', '🍅'],
  ['potato', '🥔'],
  ['carrot', '🥕'],
  ['onion', '🧅'],
  ['garlic', '🧄'],
  ['pepper', '🫑'],
  ['corn', '🌽'],
  ['cucumber', '🥒'],
  ['lettuce', '🥬'],
  ['broccoli', '🥦'],
  ['mushroom', '🍄'],
  ['salad', '🥗'],
  // Protein.
  ['chicken', '🍗'],
  ['turkey', '🍗'],
  ['beef', '🥩'],
  ['steak', '🥩'],
  ['pork', '🥩'],
  ['bacon', '🥓'],
  ['sausage', '🌭'],
  ['fish', '🐟'],
  ['salmon', '🐟'],
  ['shrimp', '🦐'],
  ['tofu', '🍢'],
  // Snacks / drinks / frozen.
  ['chocolate', '🍫'],
  ['cookie', '🍪'],
  ['chips', '🍟'],
  ['popcorn', '🍿'],
  ['ice cream', '🍨'],
  ['pizza', '🍕'],
  ['juice', '🧃'],
  ['water', '💧'],
  ['soda', '🥤'],
  ['wine', '🍷'],
  ['beer', '🍺'],
] as const;

// Broad fallback by product category when no name keyword hits — keeps the
// column varied (a Produce item still reads as produce, not a basket).
const CATEGORY_EMOJI: ReadonlyArray<readonly [string, string]> = [
  ['dairy', '🥛'],
  ['egg', '🥚'],
  ['produce', '🥦'],
  ['fruit', '🍎'],
  ['vegetable', '🥦'],
  ['bakery', '🍞'],
  ['meat', '🥩'],
  ['seafood', '🐟'],
  ['frozen', '🧊'],
  ['beverage', '🥤'],
  ['drink', '🥤'],
  ['snack', '🍪'],
  ['pantry', '🥫'],
  ['household', '🧻'],
] as const;

export function shoppingListItemThumb(item: ShoppingListItemWithDetails): string {
  const name = (item.product_name ?? item.display_title ?? '')
    .trim()
    .toLowerCase();
  for (const [needle, emoji] of NAME_EMOJI) {
    if (name.includes(needle)) return emoji;
  }
  const category = (item.category_name ?? '').trim().toLowerCase();
  for (const [needle, emoji] of CATEGORY_EMOJI) {
    if (category.includes(needle)) return emoji;
  }
  return '🛒';
}
